/* eslint-disable @typescript-eslint/no-unsafe-assignment */

import * as React from 'react'
import { Formik, Form, FieldMetaProps } from 'formik'
import Dialog from '@material-ui/core/Dialog'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Button from '@material-ui/core/Button'
import { IIncident } from '../../../models/Incident'
import { logError } from '../../../logging'
import { FormikField } from '../../form_controls/FormikField'
import { FormControlLabel, Grid, Typography } from '@material-ui/core'
import { GridProps } from '@material-ui/core/Grid'
import { UserFacingError } from '../../../errors/UserFacingError'
import { IEmailFields } from '../../../models/EmailTypes'
import TextChipListControl from '../../form_controls/TextChipListControl'
import { PageLoader } from '../../common/PageLoader'
import TextControl from '../../form_controls/TextControl'
import TextAreaControl from '../../form_controls/TextAreaControl'
import CheckboxControl from '../../form_controls/CheckboxControl'
import {
  IOneplaceLibraryContextProp,
  withOneplaceLibraryContext
} from '../../OneplaceLibraryProvider'
import { IUserCapabilities } from '../../../models/User'

export interface IIncidentEmailDialogProps extends IOneplaceLibraryContextProp {
  isOpen: boolean
  incident: IIncident
  capabilities: IUserCapabilities
  franchiseeId: number
  onClose: () => void
  onSubmitDialogsCompleted: () => void
  prePopulatedEmails?: string
  goToDashboard: () => void
}

export interface IIncidentEmailDialogState {
  stage:
    | 'loading'
    | 'edit_email'
    | 'sending'
    | 'sent'
    | 'error'
    | 'submit_error'
  initialValues: IEmailFields
  emailOptions: { [key: string]: boolean }
  sendEnabled: boolean
  submitError: string
}

/* eslint-disable react/display-name */
export const IncidentEmailDialog = withOneplaceLibraryContext(
  class extends React.Component<
    IIncidentEmailDialogProps,
    IIncidentEmailDialogState
  > {
    messageContent: IEmailFields = null as any

    constructor(props: IIncidentEmailDialogProps) {
      super(props)
      this.onValidate = this.onValidate.bind(this)
      this.onSendClicked = this.onSendClicked.bind(this)
      this.onClose = this.onClose.bind(this)
      this.onSubmitErrorClose = this.onSubmitErrorClose.bind(this)

      this.state = {
        stage: 'loading',
        initialValues: null as any,
        sendEnabled: true,
        submitError: '',
        emailOptions: {
          sendPDF: false,
          sendURLSign: false,
          invalidateURL: false
        }
      }
      void this.loadTemplate()
    }

    async loadTemplate() {
      const franchiseId = this.props.capabilities.franchiseId
      const incident = this.props.incident
      try {
        const template = await this.props.ctx.client.getIncidentEmail(
          incident.id ? incident.id : 0,
          franchiseId,
          this.props.franchiseeId
        )

        this.messageContent = template.message
        if (
          this.messageContent.toEmail &&
          incident.toEmail &&
          !incident.editEmail
        ) {
          const email: IEmailFields = template.message
          email.options = { sendPDF: true }
          try {
            await this.props.ctx.client.sendIncidentEmail(
              incident.id ? incident.id : 0,
              email,
              franchiseId,
              this.props.franchiseeId
            )
            this.setState({
              stage: 'sent'
            })
          } catch (e) {
            if (e instanceof UserFacingError) {
              this.onValidate(template.message)
              this.setState({
                stage: 'submit_error',
                submitError: e.message,
                initialValues: template.message
              })
            } else {
              logError(e, 'Error while loading incident email.')
              this.setState({
                stage: 'error'
              })
            }
          }
        } else {
          if (
            this.props.prePopulatedEmails &&
            this.props.prePopulatedEmails.trim().length > 0
          ) {
            if (!template.message.toEmail) template.message.toEmail = ''
            template.message.toEmail += `,${this.props.prePopulatedEmails}`
          }
          this.setState({
            stage: 'edit_email',
            initialValues: template.message
          })
          this.onValidate(template.message)
        }
      } catch (e) {
        if (e instanceof UserFacingError) {
          this.setState({
            stage: 'submit_error',
            submitError: e.message
          })
        } else {
          logError(e, 'Error while loading incident email.')
          this.setState({
            stage: 'error'
          })
        }
      }
    }

    onValidate(fields: IEmailFields) {
      this.messageContent = fields
      const valid = Boolean(
        fields.toEmail &&
          fields.toEmail.trim() &&
          fields.subject.trim() &&
          fields.message.trim()
      )
      if (valid != this.state.sendEnabled) {
        this.setState({ sendEnabled: valid })
      }
    }

    onClose() {
      this.props.onClose()
      this.props.onSubmitDialogsCompleted()
    }

    onSubmitErrorClose() {
      this.setState({ stage: 'edit_email' })
    }

    async onSendClicked() {
      this.messageContent.options = this.state.emailOptions
      this.setState({
        stage: 'sending'
      })
      try {
        const franchiseId = this.props.capabilities.franchiseId
        await this.props.ctx.client.sendIncidentEmail(
          this.props.incident.id ? this.props.incident.id : 0,
          this.messageContent,
          franchiseId,
          this.props.franchiseeId
        )
        this.setState({
          stage: 'sent'
        })
      } catch (e) {
        if (e instanceof UserFacingError) {
          this.setState({
            stage: 'submit_error',
            submitError: e.message,
            initialValues: this.messageContent
          })
        } else {
          logError(e, 'Error while loading incident email.')
          this.setState({
            stage: 'error'
          })
        }
      }
    }

    getMetaInfo() {
      return {
        value: null,
        touched: false,
        initialTouched: false
      } as FieldMetaProps<any>
    }

    setEmailOptions = (option: string): void => {
      this.setState({
        emailOptions: {
          ...this.state.emailOptions,
          [option]: !this.state.emailOptions[option]
        }
      })
    }

    isEmailOptionValid = (): boolean => {
      return (
        this.state.emailOptions['sendPDF'] ||
        this.state.emailOptions['sendURLSign']
      )
    }

    render() {
      const t = this.props.ctx.i18next.t
      const gridProps: GridProps = { xs: 12 }

      return (
        <Dialog
          open={this.props.isOpen}
          PaperProps={{ style: { width: '95%', maxWidth: 700 } }}
          disableBackdropClick={true}
        >
          <DialogTitle>{t('email_incident')}</DialogTitle>
          <DialogContent>
            {(this.state.stage == 'loading' ||
              this.state.stage == 'sending') && (
              <PageLoader loading={true} paddingTop={0} />
            )}
            {this.state.stage == 'edit_email' && (
              <>
                <Formik
                  initialValues={this.state.initialValues}
                  onSubmit={(_values, _actions) => {
                    return
                  }}
                  validate={this.onValidate}
                  render={() => (
                    <Form>
                      <Grid container spacing={2}>
                        <FormikField
                          name='toEmail'
                          label={t('email_to')}
                          component={TextChipListControl}
                          gridProps={gridProps}
                        />
                        <FormikField
                          name='ccEmail'
                          label={t('email_cc')}
                          component={TextChipListControl}
                          gridProps={gridProps}
                        />
                        <FormikField
                          name='subject'
                          label={t('email_subject')}
                          component={TextControl}
                          gridProps={gridProps}
                        />
                        <FormikField
                          name='message'
                          label={t('email_message')}
                          component={TextAreaControl}
                          gridProps={gridProps}
                        />
                      </Grid>
                    </Form>
                  )}
                />

                <div style={{ marginTop: '25px' }}>
                  <Typography variant='subtitle1' display='inline'>
                    {t('incident_email_include')}
                  </Typography>
                  <Typography variant='caption' display='inline'>
                    {t('incident_email_include_tip')}
                  </Typography>
                  <div>
                    <FormControlLabel
                      control={
                        <CheckboxControl
                          id='includePDF'
                          field={
                            {
                              name: 'includePDF',
                              value: this.state.emailOptions['sendPDF']
                            } as any
                          }
                          form={
                            {
                              setFieldValue: () =>
                                this.setEmailOptions('sendPDF')
                            } as any
                          }
                          meta={this.getMetaInfo()}
                        />
                      }
                      label={t('incident_email_include_pdf')}
                    />
                  </div>
                  <div>
                    <FormControlLabel
                      control={
                        <CheckboxControl
                          id='includeLink'
                          field={
                            {
                              name: 'includeLink',
                              value: this.state.emailOptions['sendURLSign']
                            } as any
                          }
                          form={
                            {
                              setFieldValue: () =>
                                this.setEmailOptions('sendURLSign')
                            } as any
                          }
                          meta={this.getMetaInfo()}
                        />
                      }
                      label={t('incident_email_include_link')}
                    />
                  </div>
                  {this.props.incident.path &&
                    this.state.emailOptions['sendURLSign'] && (
                      <div style={{ marginLeft: '32px' }}>
                        <FormControlLabel
                          control={
                            <CheckboxControl
                              id='replaceLink'
                              field={
                                {
                                  name: 'replaceLink',
                                  value:
                                    this.state.emailOptions['invalidateURL']
                                } as any
                              }
                              form={
                                {
                                  setFieldValue: () =>
                                    this.setEmailOptions('invalidateURL')
                                } as any
                              }
                              meta={this.getMetaInfo()}
                            />
                          }
                          label={t('incident_toemail_invalidate')}
                        />
                      </div>
                    )}
                </div>
              </>
            )}
            {this.state.stage == 'error' && (
              <PageLoader
                loading={false}
                paddingTop={0}
                status={t('email_process_error')}
              />
            )}
            {this.state.stage == 'sent' && (
              <PageLoader
                loading={false}
                paddingTop={0}
                status={t('email_sent_successfully')}
              />
            )}
            {this.state.stage == 'submit_error' && (
              <PageLoader
                loading={false}
                paddingTop={0}
                status={this.state.submitError}
              />
            )}
          </DialogContent>
          <DialogActions>
            {this.state.stage == 'edit_email' && (
              <>
                <Button onClick={this.onClose} color='primary'>
                  {t('cancel')}
                </Button>
                <Button
                  disabled={
                    !this.state.sendEnabled || !this.isEmailOptionValid()
                  }
                  onClick={this.onSendClicked}
                  color='primary'
                >
                  {t('send_email')}
                </Button>
              </>
            )}
            {this.state.stage == 'error' && (
              <>
                <Button onClick={this.onClose} color='primary'>
                  {t('close')}
                </Button>
              </>
            )}
            {this.state.stage == 'submit_error' && (
              <>
                <Button onClick={this.onSubmitErrorClose} color='primary'>
                  {t('close')}
                </Button>
              </>
            )}
            {this.state.stage == 'sent' && (
              <>
                <Button onClick={this.onClose} color='primary'>
                  {t('continue_editing')}
                </Button>
                <Button onClick={this.props.goToDashboard} color='primary'>
                  {t('go_to_dashboard')}
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>
      )
    }
  }
)
